/**
 * Shared Tailwind class strings for feed list cards + thread OP articles (club and forum surfaces).
 * Export names retain `clubPost*` / `clubFeed*` prefixes for now to avoid a wide token rename churn;
 * this module is the shared presentation source of truth regardless of route.
 */

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

/**
 * Feed column: centered readable width on md+ so cards do not stretch edge-to-edge on wide layouts.
 * Full width below md so phones keep natural gutters from the page padding only.
 */
export const clubFeedColumnShellClass =
  "w-full min-w-0 md:max-w-3xl lg:max-w-[52rem] md:mx-auto";

export const clubPostCardArticleClass =
  "rounded-2xl border border-white/14 bg-gradient-to-b from-white/[0.1] to-white/[0.04] backdrop-blur-md shadow-[0_28px_90px_rgba(0,0,0,0.55)] overflow-hidden ring-1 ring-white/[0.08] w-full min-w-0 max-w-full box-border";

/** Subtle hover on feed list cards only (thread page omits this). */
export const clubPostCardFeedListHoverClass =
  "transition-[border-color,box-shadow] duration-200 hover:border-white/18 hover:shadow-[0_32px_96px_rgba(0,0,0,0.52)]";

export const clubPostCardHeaderClass =
  "px-3 sm:px-5 md:px-6 pt-4 sm:pt-5 pb-3 border-b border-white/10 w-full min-w-0 max-w-full box-border";

export const clubPostCardBodyClass =
  "px-3 sm:px-5 md:px-6 py-4 sm:py-6 md:py-7 w-full min-w-0 max-w-full box-border";

/** Slightly tighter vertical padding for feed list cards (compact scroll). */
export const clubPostCardBodyFeedClass =
  "px-3 sm:px-5 md:px-6 py-3.5 sm:py-4 md:py-5 w-full min-w-0 max-w-full box-border";

export const clubPostCardAvatarClass =
  "h-10 w-10 sm:h-11 sm:w-11 rounded-xl border border-white/12 bg-gradient-to-br from-white/[0.12] to-black/40 overflow-hidden shrink-0 grid place-items-center text-sm font-semibold text-white ring-1 ring-white/10";

/** Thread page post title (h1). */
export const clubPostCardTitleClass =
  "text-xl sm:text-2xl md:text-[1.75rem] font-semibold text-white leading-snug tracking-tight break-words";

/** Feed list cards: slightly smaller title, same hierarchy. */
export const clubPostCardTitleFeedClass =
  "text-lg sm:text-xl md:text-[1.55rem] font-semibold text-white leading-snug tracking-tight break-words";

export const clubPostCardBodyTextClass =
  "text-[15px] sm:text-base text-gray-200/95 whitespace-pre-line leading-[1.65] break-words";

export const clubPostCardActionRowClass =
  "px-4 sm:px-5 md:px-6 py-3.5 border-t border-white/10 bg-black/[0.12] w-full min-w-0 max-w-full box-border";

export const CLUB_FEED_ACTION_SHADOW = "shadow-[0_4px_14px_rgba(0,0,0,0.22)]";

export function clubFeedHeartClass(accent, liked) {
  return "text-white/70 border-white/12 bg-white/[0.02] hover:bg-white/[0.06]";
}

export const clubFeedActionBase =
  "shrink-0 inline-flex items-center gap-2 h-9 px-4 rounded-xl border text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/25 focus-visible:ring-offset-2 focus-visible:ring-offset-[#050810]";

export function clubFeedNeutralButtonClass() {
  return cn(
    clubFeedActionBase,
    "border-white/12 bg-white/[0.02] text-white/75 hover:bg-white/[0.06]",
    CLUB_FEED_ACTION_SHADOW
  );
}

export function clubPostOptionsTriggerClass(menuOpen) {
  return cn(
    "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/12 bg-white/[0.02] text-white/75 transition",
    CLUB_FEED_ACTION_SHADOW,
    "hover:bg-white/[0.06] hover:text-white/90",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/25 focus-visible:ring-offset-2 focus-visible:ring-offset-[#050810]",
    menuOpen && "bg-white/[0.06] border-white/16 text-white"
  );
}

export const clubPostOptionsMenuClass =
  "absolute right-0 top-full z-[50] mt-1.5 min-w-[12rem] rounded-xl border border-white/[0.12] bg-[#080c14]/98 backdrop-blur-xl shadow-[0_16px_48px_rgba(0,0,0,0.55)] overflow-hidden py-1 ring-1 ring-white/[0.06]";

/** Primary image block (thread + feed). Feed may pass extra max-h via className. */
export const clubPostPrimaryMediaFrameClass =
  "relative w-full min-w-0 max-w-full rounded-2xl border border-white/12 overflow-hidden bg-black/25 ring-1 ring-inset ring-white/5 shadow-lg";

export const clubPostPrimaryMediaInnerBtnClass =
  "w-full min-w-0 max-w-full flex items-center justify-center bg-gradient-to-b from-white/[0.04] to-black/45 p-1.5 sm:p-3 box-border";

export const clubPostExpandImageBtnClass =
  "absolute top-3 right-3 z-10 h-10 w-10 rounded-xl border border-white/18 bg-black/65 backdrop-blur-md text-white/95 hover:bg-black/80 inline-flex items-center justify-center shadow-lg transition-colors";
